/**
 * Pure helpers for the detached job chat window (a normal browser pop-up the admin can put on any screen).
 * No window access here except through the arguments, so the maths is testable on its own (see
 * live-chat-drag.ts for the same split).
 */

/**
 * Viewport size wanted for the bubble state: as small as a pop-up gets. The browser enforces its own minimum (measured
 * in Chrome: inner about 130 x 62, outer 144 x 190 with the title and address strips), so this is a wish, not a promise.
 */
export const PILL_INNER = { w: 132, h: 84 } as const;

/** Rough height of a pop-up's title + address strips, to put the bubble under the pointer when it is dropped. */
const POPUP_CHROME_GUESS = { w: 14, h: 90 } as const;

/** Viewport size of the expanded chat: the same as the pop-out has always opened at. */
export const EXPANDED_DEFAULT = { w: 1240, h: 820 } as const;

/** The expanded window is never made smaller than this by us (the admin can still drag it smaller). */
export const EXPANDED_MIN = { w: 720, h: 520 } as const;

export const DETACH_BOUNDS_KEY = 'rfi.liveChat.detach';

export interface DetachBounds {
  /** Screen position of the window's top-left corner when it was last left (blur / close). */
  x?: number;
  y?: number;
  /** Viewport size of the expanded window when it was last left. */
  expW: number;
  expH: number;
  mode: 'pill' | 'expanded';
}

/**
 * `window.open` feature string. `left` / `top` are screen coordinates; the browser pulls them onto a visible screen.
 * The window stays `resizable` (the admin may size it), but asks for no menu / tool / address / status strips so it
 * opens as a clean chat window (Nahid/Ali, 4 Nov meeting). Browsers treat these as hints — `popup=yes` already drops
 * most chrome in Chromium, and the OS title bar (with its own minimise / maximise) cannot be removed from a pop-up;
 * only the Picture-in-Picture bubble is truly chromeless.
 */
export function detachFeatures(size: { w: number; h: number }, at?: { x: number; y: number } | null): string {
  const pos = at && Number.isFinite(at.x) && Number.isFinite(at.y) ? `,left=${Math.round(at.x)},top=${Math.round(at.y)}` : '';
  return `popup=yes,width=${Math.round(size.w)},height=${Math.round(size.h)}${pos},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`;
}

/** `resizeBy` deltas that turn the current viewport size into the wanted one (`resizeTo` takes the OUTER size). */
export function resizeDelta(
  current: { w: number; h: number },
  wanted: { w: number; h: number },
): { dw: number; dh: number } {
  return { dw: Math.round(wanted.w - current.w), dh: Math.round(wanted.h - current.h) };
}

/** True when a pointer at (clientX, clientY) is outside a viewport of vw x vh. The border itself is inside. */
export function isOutsideViewport(clientX: number, clientY: number, vw: number, vh: number): boolean {
  return clientX < 0 || clientY < 0 || clientX > vw || clientY > vh;
}

/** Where the bubble window opens so the pointer is over the bubble: `screenX` / `screenY` are the pointer's screen position. */
export function pillOriginAtPointer(screenX: number, screenY: number): { x: number; y: number } {
  return {
    x: screenX - (PILL_INNER.w + POPUP_CHROME_GUESS.w) / 2,
    y: screenY - (POPUP_CHROME_GUESS.h + PILL_INNER.h / 2),
  };
}

/**
 * Centre a window of `size` on the usable screen `area`, so a first-time pop-out (no remembered place) opens in the
 * middle of the screen instead of at the browser's own offset-from-opener spot (Nahid/Ali, 4 Nov: a cleaner default).
 */
export function centeredOrigin(
  size: { w: number; h: number },
  area: { left?: number; top?: number; width: number; height: number },
): { x: number; y: number } {
  const left = area.left ?? 0;
  const top = area.top ?? 0;
  return {
    x: Math.round(left + Math.max(0, (area.width - size.w) / 2)),
    y: Math.round(top + Math.max(0, (area.height - size.h) / 2)),
  };
}

/**
 * Where a window of w x h at (x, y) must move so it lies inside `area` (a screen's usable part). A window bigger than the
 * area sits at the area's top-left. Used after the pill grows into the chat, which can push it past the screen's edge.
 */
export function keepOnScreen(
  win: { x: number; y: number; w: number; h: number },
  area: { left: number; top: number; width: number; height: number },
): { x: number; y: number } {
  const maxX = area.left + area.width - win.w;
  const maxY = area.top + area.height - win.h;
  return {
    x: Math.round(Math.max(area.left, Math.min(win.x, maxX))),
    y: Math.round(Math.max(area.top, Math.min(win.y, maxY))),
  };
}

export function readDetachBounds(storage: Pick<Storage, 'getItem'> | null | undefined): DetachBounds | null {
  try {
    const raw = storage?.getItem(DETACH_BOUNDS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<DetachBounds> | null;
    if (!p || typeof p !== 'object') return null;
    const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
    if (!num(p.expW) || !num(p.expH) || (p.mode !== 'pill' && p.mode !== 'expanded')) return null;
    return {
      expW: Math.max(EXPANDED_MIN.w, p.expW),
      expH: Math.max(EXPANDED_MIN.h, p.expH),
      mode: p.mode,
      ...(num(p.x) && num(p.y) ? { x: p.x, y: p.y } : {}),
    };
  } catch {
    return null;
  }
}

export function writeDetachBounds(storage: Pick<Storage, 'setItem'> | null | undefined, bounds: DetachBounds): void {
  try {
    storage?.setItem(DETACH_BOUNDS_KEY, JSON.stringify(bounds));
  } catch {
    /* storage blocked or full: the window just opens at its default place next time */
  }
}
